import React, { useMemo } from 'react';
import { mkNode, mkEdge, FlowCanvas } from './flowParts';

// Workflow diagrams for the Edit → Color → Publish pipeline, drawn with the same
// React Flow building blocks as the modules map so they stay consistent. Each
// diagram mirrors the actual code path in backend/api/render.js + the grade
// engine, so keep them in sync when the pipeline changes.

const C = {
  src: '#3fbf6f', // sources / storage (green)
  edit: '#9b6cf2', // editing / grade (purple)
  state: '#e0a414', // draft / state (amber)
  render: '#e0517a', // render workers (pink)
  out: '#5b8def', // outputs / encode (blue)
  join: '#14e0c8', // compose / join (teal)
};

// ---- 1. High-level edit → publish workflow -------------------------------
const FLOW_NODES = [
  mkNode('upload', 0, 170, 'Upload footage', 'RAW photo · MP4 · RED .R3D', C.src),
  mkNode('cliplook', 300, 50, 'Clip look (optional)', '“Edit color & light” → asset.grade', C.edit, 220),
  mkNode('addmovie', 300, 290, 'Add to a movie', 'arrives with its saved look', C.edit, 220),
  mkNode('timeline', 640, 170, 'Movie editor', 'trim · speed · clip + movie grade · music · fades', C.edit, 230),
  mkNode('draft', 980, 170, 'Draft saved', 'no render · “unpublished” pill tracks drift', C.state, 210),
  mkNode('preview', 1280, 40, 'Build preview', 'flat master MP4 (ungraded)', C.render, 200),
  mkNode('publish', 1280, 300, 'Publish', 'HLS render + grade bake', C.render, 200),
  mkNode('flat', 1580, 40, 'Flat master', 'Reframe · Social · Quick Compare', C.out, 200),
  mkNode('hls', 1580, 200, 'HLS ladder', 'graded · adaptive 1080/720/480', C.out, 200),
  mkNode('dl', 1580, 360, 'Download master', 'graded high-bitrate MP4', C.out, 200),
];

const FLOW_EDGES = [
  mkEdge('upload', 'cliplook', 'grade source', { color: C.edit }),
  mkEdge('upload', 'addmovie', '', { color: C.src }),
  mkEdge('cliplook', 'addmovie', 'saved look', { color: C.edit }),
  mkEdge('addmovie', 'timeline', '', { color: C.edit }),
  mkEdge('timeline', 'draft', '', { animated: true, color: C.state }),
  mkEdge('draft', 'preview', 'fast', { color: C.render }),
  mkEdge('draft', 'publish', 'share', { color: C.render }),
  mkEdge('preview', 'flat', '', { color: C.out }),
  mkEdge('publish', 'hls', '', { color: C.out }),
  mkEdge('publish', 'dl', '', { color: C.out }),
];

const FLOW_LEGEND = [
  ['Sources', C.src],
  ['Edit / grade', C.edit],
  ['Draft state', C.state],
  ['Render', C.render],
  ['Outputs', C.out],
];

export function EditPublishFlow() {
  const nodes = useMemo(() => FLOW_NODES, []);
  const edges = useMemo(() => FLOW_EDGES, []);
  return <FlowCanvas nodes={nodes} edges={edges} legend={FLOW_LEGEND} height={420} />;
}

// ---- 2. The render filtergraph (ffmpeg node order) -----------------------
const GRAPH_NODES = [
  // Clip 0 lane.
  mkNode('in0', 0, 0, '[0:v] clip 0', 'source window (-ss / -t)', C.src, 180),
  mkNode('norm0', 230, 0, 'normalize', 'setpts · scale · pad · fps · yuv420p', C.out, 210),
  mkNode('sp0', 500, 0, 'SETPARAMS_709', 'pin Rec.709 limited', C.out, 190),
  mkNode('clut0', 760, 0, 'clip LUT + spatial', 'asset.grade — layer 1', C.edit, 210),
  // Clip 1 lane.
  mkNode('in1', 0, 150, '[1:v] clip 1', 'source window (-ss / -t)', C.src, 180),
  mkNode('norm1', 230, 150, 'normalize', 'setpts · scale · pad · fps · yuv420p', C.out, 210),
  mkNode('sp1', 500, 150, 'SETPARAMS_709', 'pin Rec.709 limited', C.out, 190),
  mkNode('clut1', 760, 150, 'clip LUT + spatial', 'asset.grade — layer 1', C.edit, 210),
  // Join → movie grade → encode.
  mkNode('join', 1050, 75, 'xfade / concat', 'cross-dissolve or hard cut', C.join, 200),
  mkNode('fades', 1320, 75, 'fade in / out', 'to black', C.join, 170),
  mkNode('mgrade', 1550, 75, 'movie LUT + spatial', 'movie.grade — layer 2 (HLS only)', C.render, 230),
  mkNode('split', 1840, 75, 'split → ladder', '1080 / 720 / 480', C.state, 180),
  mkNode('enc', 2080, 75, 'libx264 high', 'slow · crf 18 · maxrate/bufsize · Rec.709 tags', C.state, 250),
  mkNode('hls', 2380, 75, 'HLS segments', 'GOP 2s · faststart', C.out, 190),
  // Audio lane.
  mkNode('a0', 0, 320, '[*:a] audio', 'aresample · atempo', C.src, 190),
  mkNode('amix', 1050, 320, 'crossfade + song mix', 'sidechain duck under songs', C.join, 230),
  mkNode('aac', 2080, 320, 'aac 192k', 'stereo 48k', C.state, 170),
];

const GRAPH_EDGES = [
  mkEdge('in0', 'norm0', '', { color: C.src }),
  mkEdge('norm0', 'sp0', '', { color: C.out }),
  mkEdge('sp0', 'clut0', '', { color: C.out }),
  mkEdge('clut0', 'join', '[v0]', { color: C.edit }),
  mkEdge('in1', 'norm1', '', { color: C.src }),
  mkEdge('norm1', 'sp1', '', { color: C.out }),
  mkEdge('sp1', 'clut1', '', { color: C.out }),
  mkEdge('clut1', 'join', '[v1]', { color: C.edit }),
  mkEdge('join', 'fades', '', { color: C.join }),
  mkEdge('fades', 'mgrade', '', { color: C.join }),
  mkEdge('mgrade', 'split', '', { color: C.render }),
  mkEdge('split', 'enc', '', { color: C.state }),
  mkEdge('enc', 'hls', '', { color: C.out }),
  mkEdge('a0', 'amix', '', { color: C.src }),
  mkEdge('amix', 'aac', '', { color: C.join }),
  mkEdge('aac', 'hls', 'mux', { animated: true, color: C.out }),
];

const GRAPH_LEGEND = [
  ['Input', C.src],
  ['Normalize + color mgmt', C.out],
  ['Clip grade (layer 1)', C.edit],
  ['Join / fades', C.join],
  ['Movie grade (layer 2)', C.render],
  ['Encode', C.state],
];

export function RenderFilterGraph() {
  const nodes = useMemo(() => GRAPH_NODES, []);
  const edges = useMemo(() => GRAPH_EDGES, []);
  return <FlowCanvas nodes={nodes} edges={edges} legend={GRAPH_LEGEND} height={460} />;
}

// ---- 3. The double-layer grade + WYSIWYG preview -------------------------
const LAYER_NODES = [
  mkNode('lsrc', 0, 70, 'Source pixels', 'normalized to Rec.709', C.src, 190),
  mkNode('l1', 300, 70, 'Layer 1 · Clip grade', 'asset.grade — LUT + spatial', C.edit, 230),
  mkNode('l2', 660, 70, 'Layer 2 · Movie grade', 'movie.grade — LUT + spatial', C.render, 230),
  mkNode('pub', 1020, 70, 'Published pixels', 'baked into HLS / master', C.out, 200),
  // Preview path.
  mkNode('studio', 300, 280, 'Color Studio (WebGL)', 'live GPU preview', C.join, 220),
  mkNode('pair', 660, 280, 'compileLutPair(clip, movie)', 'single composite 3D LUT', C.join, 240),
  mkNode('match', 1020, 280, 'Preview == Published', 'WYSIWYG', C.join, 200),
];

const LAYER_EDGES = [
  mkEdge('lsrc', 'l1', '', { color: C.edit }),
  mkEdge('l1', 'l2', 'stacked', { color: C.render }),
  mkEdge('l2', 'pub', '', { color: C.out }),
  mkEdge('lsrc', 'studio', 'same source', { color: C.join }),
  mkEdge('studio', 'pair', '', { color: C.join }),
  mkEdge('pair', 'match', '', { color: C.join }),
  mkEdge('match', 'pub', 'matches', { animated: true, color: C.out }),
];

const LAYER_LEGEND = [
  ['Source', C.src],
  ['Clip grade (layer 1)', C.edit],
  ['Movie grade (layer 2)', C.render],
  ['Published', C.out],
  ['Studio preview', C.join],
];

export function GradeLayers() {
  const nodes = useMemo(() => LAYER_NODES, []);
  const edges = useMemo(() => LAYER_EDGES, []);
  return <FlowCanvas nodes={nodes} edges={edges} legend={LAYER_LEGEND} height={380} />;
}

// ---- Compact, clickable navigation map -----------------------------------
// A vertically efficient diagram that doubles as the page's table of contents:
// click any node to jump to (and expand) that group. Static — no pan/zoom — so
// the page keeps scrolling normally over it.
const NAV_W = 158;
const NAV_NODES = [
  mkNode('pipeline', 0, 0, 'Edit → Color → Publish', 'the publish pipeline', C.render, 184),
  mkNode('grp-app', 230, 0, 'Web app & data', '', C.out, NAV_W),
  mkNode('grp-uploads', 420, 0, 'Uploads & storage', '', C.src, NAV_W),
  mkNode('grp-edit', 610, 0, 'Editing', '', C.edit, NAV_W),
  mkNode('grp-color', 800, 0, 'Color & light', '', C.edit, NAV_W),
  mkNode('grp-playback', 990, 0, 'Streaming', '', C.out, NAV_W),
  mkNode('grp-social', 85, 92, 'Social', '', C.join, NAV_W),
  mkNode('grp-media', 275, 92, 'Media gen', '', C.join, NAV_W),
  mkNode('grp-compute', 465, 92, 'RED & compute', '', C.render, NAV_W),
  mkNode('grp-surf', 655, 92, 'Surf intel', '', C.state, NAV_W),
  mkNode('grp-arch', 845, 92, 'Architecture map', '', C.state, NAV_W),
  mkNode('grabgrade', 275, 184, 'Frame grab & grade', '16-bit · perception · region', C.edit, NAV_W),
  mkNode('forecastsci', 655, 184, 'Forecast science', 'data · physics · calibration', C.state, NAV_W),
];
const navLink = (s, t) => mkEdge(s, t, '', { color: 'rgba(255,255,255,0.14)' });
const NAV_EDGES = [
  navLink('pipeline', 'grp-edit'),
  navLink('grp-app', 'grp-uploads'),
  navLink('grp-uploads', 'grp-edit'),
  navLink('grp-edit', 'grp-color'),
  navLink('grp-color', 'grp-playback'),
  navLink('pipeline', 'grabgrade'),
  navLink('grp-surf', 'forecastsci'),
];

export function DocsNav({ onNavigate }) {
  const nodes = useMemo(() => NAV_NODES, []);
  const edges = useMemo(() => NAV_EDGES, []);
  const handle = (_e, node) => onNavigate && onNavigate(node.id);
  return (
    <FlowCanvas
      nodes={nodes}
      edges={edges}
      height={228}
      interactive={false}
      onNodeClick={handle}
      fitPadding={0.06}
    />
  );
}
